<script lang="ts" setup>
import {
  useUmbracoLoggedInState,
  useHeadlessDataState,
  watch,
  ref,
  computed,
  useDebugMode,
} from "#imports";

const pinned = ref( true );

const { enabled: debugMode, toggle: toggleDebug } = useDebugMode();

const isPreview = computed( () => {
  if ( import.meta.server ) {
    return true;
  } else {
    const queryParams = new URLSearchParams( window.location.search );
    const isPreview = queryParams.get( "preview" );
    return isPreview || window.location.pathname.startsWith( "/umb-backoffice-component-preview" );
  }
} );

const isLocalhost = computed( () => {
  if ( import.meta.server ) {
    return false;
  } else {
    return window.location.hostname === "localhost";
  }
} );

const data = useHeadlessDataState();

const toolsEnabled = ref<boolean>( false );

const editButtonEnabled = ref<boolean>( false );
const editButtonTarget = ref<string | null>( null );
const editButtonType = ref<string | null>( null );

const knownTypes = [
  "content",
  "media",
  "member",
];

function updateEditButton () {
  const type = data.value?.type;
  const id = data.value?.data?.id;

  // Show the edit button if we have a known type and a valid ID
  if ( data.value && type && knownTypes.includes( type ) && id ) {
    editButtonEnabled.value = true;
    editButtonTarget.value = `/umbraco#/${type}/${type}/edit/${id}`;
    editButtonType.value = type;
  } else {
    editButtonEnabled.value = false;
    editButtonTarget.value = null;
    editButtonType.value = null;
  }
}

function togglePin () {
  pinned.value = !pinned.value;
}

watch( data, updateEditButton, { deep: true } );
updateEditButton();

toolsEnabled.value = useUmbracoLoggedInState().value || isLocalhost.value || debugMode.value;
</script>

<template>
  <client-only>
    <div
      v-if="toolsEnabled && !isPreview"
      id="umbraco-tools"
      class="umbraco-tools"
      :class="{ 'umbraco-tools--pinned': pinned }">
      <div class="umbraco-tools__content">
        <a
          v-tooltip.left="'Open Umbraco'"
          href="/umbraco"
          target="_blank"
          rel="noopener">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32">
            <path
              d="M0 16c0-8.636 7.151-15.573 16-15.573s16 6.937 16 15.573c0 8.636-7.151 15.573-16 15.573s-16-6.937-16-15.573zM15.645 21.307c-1.557 0-2.833-0.14-3.681-0.28-0.991-0.215-1.631-0.781-2.052-1.557-0.355-0.781-0.568-2.053-0.496-3.756 0-0.916 0.068-1.765 0.136-2.62 0.145-0.843 0.213-1.552 0.36-2.124l0.14-0.563v-0.068c0-0.141-0.14-0.287-0.287-0.287l-2.052-0.281h-0.068c-0.14 0-0.285 0.141-0.353 0.213 0 0.136-0.073 0.209-0.141 0.495-0.14 0.636-0.213 1.204-0.353 1.98-0.147 0.911-0.219 1.839-0.213 2.76 0 0-0.068 0.213 0 1.912 0.072 1.703 0.353 3.047 0.921 4.109 0.568 0.989 1.484 1.765 2.833 2.192 1.344 0.428 3.183 0.641 5.521 0.641h0.28c2.339 0 4.177-0.213 5.521-0.641 1.349-0.421 2.271-1.203 2.833-2.192 0.568-1.063 0.855-2.407 0.921-4.109 0.073-1.699 0-1.912 0-1.912 0-0.989-0.135-1.912-0.213-2.755-0.14-0.855-0.208-1.423-0.353-1.985-0.068-0.281-0.068-0.427-0.141-0.495 0-0.14-0.14-0.213-0.353-0.213h-0.068l-2.057 0.281c-0.141 0-0.281 0.145-0.281 0.287v0.068l0.14 0.567c0.141 0.568 0.209 1.277 0.355 2.12 0.141 0.855 0.141 1.704 0.141 2.62 0 1.703-0.141 2.907-0.496 3.756-0.353 0.781-1.061 1.343-2.052 1.557-0.853 0.213-2.124 0.28-3.681 0.28z" />
          </svg>
        </a>

        <component
          :is="editButtonEnabled ? 'a' : 'button'"
          v-tooltip.left="editButtonEnabled ? `Edit ${editButtonType} in Umbraco`: null"
          :href="editButtonEnabled ? editButtonTarget : null"
          :disabled="!editButtonEnabled"
          target="_blank"
          rel="noopener"
          :class="editButtonEnabled ? '' : 'disabled'">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512">
            <path
              d="M410.3 231l11.3-11.3-33.9-33.9-62.1-62.1L291.7 89.8l-11.3 11.3-22.6 22.6L58.6 322.9c-10.4 10.4-18 23.3-22.2 37.4L1 480.7c-2.5 8.4-.2 17.5 6.1 23.7s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L387.7 253.7 410.3 231zM160 399.4l-9.1 22.7c-4 3.1-8.5 5.4-13.3 6.9L59.4 452l23-78.1c1.4-4.9 3.8-9.4 6.9-13.3l22.7-9.1v32c0 8.8 7.2 16 16 16h32zM362.7 18.7L348.3 33.2 325.7 55.8 314.3 67.1l33.9 33.9 62.1 62.1 33.9 33.9 11.3-11.3 22.6-22.6 14.5-14.5c25-25 25-65.5 0-90.5L453.3 18.7c-25-25-65.5-25-90.5 0zm-47.4 168l-144 144c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l144-144c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z" />
          </svg>
        </component>

        <UmbracoCultureToggle
          class="umbraco-tools__culture-flag" />

        <button
          v-if="isLocalhost"
          v-tooltip.left="debugMode ? 'Metrics enabled' : 'Metrics disabled'"
          @click="toggleDebug">
          <svg
            v-if="debugMode"
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            viewBox="0 0 512 512">
            <path
              d="M256 0c53 0 96 43 96 96v3.6c0 15.7-12.7 28.4-28.4 28.4H188.4c-15.7 0-28.4-12.7-28.4-28.4V96c0-53 43-96 96-96zM41.4 105.4c12.5-12.5 32.8-12.5 45.3 0l64 64c.7 .7 1.3 1.4 1.9 2.1c14.2-7.3 30.4-11.4 47.5-11.4H312c17.1 0 33.2 4.1 47.5 11.4c.6-.7 1.2-1.4 1.9-2.1l64-64c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3l-64 64c-.7 .7-1.4 1.3-2.1 1.9c6.2 12 10.1 25.3 11.1 39.5H480c17.7 0 32 14.3 32 32s-14.3 32-32 32H416c0 24.6-5.5 47.8-15.4 68.6c2.2 1.3 4.2 2.9 6 4.8l64 64c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0l-63.1-63.1c-24.5 21.8-55.8 36.2-90.3 39.6V240c0-8.8-7.2-16-16-16s-16 7.2-16 16V479.2c-34.5-3.4-65.8-17.8-90.3-39.6L86.6 502.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l64-64c1.9-1.9 3.9-3.4 6-4.8C101.5 367.8 96 344.6 96 320H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H96.3c1.1-14.1 5-27.5 11.1-39.5c-.7-.6-1.4-1.2-2.1-1.9l-64-64c-12.5-12.5-12.5-32.8 0-45.3z" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            viewBox="0 0 640 512">
            <path
              d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L477.4 348.9c1.7-9.4 2.6-19 2.6-28.9h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H479.7c-1.1-14.1-5-27.5-11.1-39.5c.7-.6 1.4-1.2 2.1-1.9l64-64c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-64 64c-.7 .7-1.3 1.4-1.9 2.1C409.2 164.1 393.1 160 376 160H264c-8.3 0-16.3 1-24 2.8L38.8 5.1zM320 0c-53 0-96 43-96 96v3.6c0 15.7 12.7 28.4 28.4 28.4H387.6c15.7 0 28.4-12.7 28.4-28.4V96c0-53-43-96-96-96zM160.3 256H96c-17.7 0-32 14.3-32 32s14.3 32 32 32h64c0 24.6 5.5 47.8 15.4 68.6c-2.2 1.3-4.2 2.9-6 4.8l-64 64c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l63.1-63.1c24.5 21.8 55.8 36.2 90.3 39.6V335.5L166.7 227.3c-3.4 9-5.6 18.7-6.4 28.7zM336 479.2c36.6-3.6 69.7-19.6 94.8-43.8L336 360.7V479.2z" />
          </svg>
        </button>

        <button
          v-tooltip.left="pinned ? 'Unpin' : 'Pin'"
          @click="togglePin">
          <svg
            class="umbraco-tools__pin"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 384 512">
            <!-- ! Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
            <path
              d="M32 32C32 14.3 46.3 0 64 0H320c17.7 0 32 14.3 32 32s-14.3 32-32 32H290.5l11.4 148.2c36.7 19.9 65.7 53.2 79.5 94.7l1 3c3.3 9.8 1.6 20.5-4.4 28.8s-15.7 13.3-26 13.3H32c-10.3 0-19.9-4.9-26-13.3s-7.7-19.1-4.4-28.8l1-3c13.8-41.5 42.8-74.8 79.5-94.7L93.5 64H64C46.3 64 32 49.7 32 32zM160 384h64v96c0 17.7-14.3 32-32 32s-32-14.3-32-32V384z" />
          </svg>
        </button>
      </div>
    </div>
  </client-only>
</template>

<style lang="scss" scoped>
.umbraco-tools {
  position: fixed;
  top: 100px;
  right: 0;
  z-index: 10000;

  &__content {
    transform: translateX(125%);
    display: flex;
    flex-direction: column;
    gap: 5px;
    background: #fff;
    border-radius: 6px 0 0 6px;
    box-shadow: -3px 5px 15px rgba(0, 0, 0, 0.2), 0 0 10px rgba(0, 0, 0, 0.2);
    padding: 3px;
    transition: transform 0.2s ease-in;
  }

  &:hover &__content {
    transform: translateX(0);
  }

  a,
  button {
    position: relative;
    padding: 4px 7px;
    border-radius: 4px;
    height: 40px;
    width: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background: none;
    cursor: pointer;

    svg {
      width: 20px;
      color: #555;
      aspect-ratio: 1 / 1;
    }

    &:disabled,
    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover {
      background: #eee;
    }

    &:active {
      background: #ddd;
    }
  }

  &--pinned &__content {
    transform: none;
  }

  &--pinned &__pin {
    transform: rotate(45deg);
  }

  &__culture-flag {
    font-size: 1.2rem;
  }
}
</style>
